import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlContainer, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TeamsService } from 'app/modules/organization/modules/people/services/teams.service';
import { BehaviorSubject, combineLatest, debounceTime, map, Observable, shareReplay, startWith } from 'rxjs';
import { Team } from 'app/models/organization/team/team';
import { MatButtonModule } from '@angular/material/button';
import { lengthValidator } from 'app/utils/validators/length';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'tm-autocomplete-select-teams',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, MatFormFieldModule, MatChipsModule, MatAutocompleteModule, MatButtonModule, MatTooltipModule],
  templateUrl: './autocomplete-select-teams.component.html',
  styleUrls: ['./autocomplete-select-teams.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true })
    }
  ]
})
export class AutocompleteSelectTeamsComponent implements OnDestroy, OnInit {
  @Input('label') label = 'Select Teams';
  @Input('required') isRequired = true;
  private readonly parentContainer = inject(ControlContainer);
  @ViewChild('teamsSearchInput') teamsSearchInput: ElementRef<HTMLInputElement>;
  readonly teamsSearchFC = new FormControl<string>('');
  @Output() init = new EventEmitter<void>();

  private readonly excludingTeams$ = new BehaviorSubject<Array<Team>>([]);
  @Input('excludingTeams') set excludingTeams(teams: Array<Team>) {
    // console.log(teams)
    if (Array.isArray(teams)) {
      this.excludingTeams$.next(teams)
    }
  }

  private readonly teamsList$ = this.teamsService.get().pipe(shareReplay({ bufferSize: 1, refCount: true }));
  teams$: Observable<Array<Team>>;
  constructor(private readonly teamsService: TeamsService) { }

  get parentForm() {
    return this.parentContainer.control as FormGroup;
  }

  get teamsFC() {
    return this.parentForm.get('teams');
  }

  displayWith(team: Team) {
    return team.name;
  }

  selectedTeams(e: MatAutocompleteSelectedEvent) {
    const team = e.option.value;
    if (team) {
      this.teamsFC.setValue([...this.teamsFC.value, team]);
      this.teamsSearchInput.nativeElement.value = '';
      this.teamsSearchFC.setValue('');
    }
  }
  removeTeam(id: string) {
    this.teamsFC.setValue([...this.teamsFC.value.filter(team => team.id !== id)]);
  }

  removeTeams() {
    this.teamsFC.setValue([]);
  }

  ngOnInit(): void {
    this.parentForm.addControl('teams', new FormControl<Array<Team>>([]));
    if (this.isRequired) {
      this.teamsFC.setValidators(lengthValidator());
      this.teamsFC.updateValueAndValidity();
    }
    this.teams$ = combineLatest({
      teams: this.teamsList$,
      teamSearch: this.teamsSearchFC.valueChanges.pipe(startWith(''), debounceTime(150)),
      teamsSelected: this.teamsFC.valueChanges.pipe(startWith([])),
      excludingTeams: this.excludingTeams$
    }).pipe(
      map(({ teams, teamSearch, teamsSelected, excludingTeams }) => {
        const searchText = (teamSearch || '').toLowerCase().trim();
        return teams.filter(team => team.name.toLowerCase().includes(searchText) && !(teamsSelected.map(teamS => teamS.id).includes(team.id)) && !(excludingTeams.map(teamS => teamS.id).includes(team.id)))
      })
    );
    this.init.emit();
  }

  ngOnDestroy(): void {
    this.parentForm.removeControl('teams');
  }
}
